<script lang="ts">
  import { debounce, isEqual } from 'lodash-es'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { normalizeStringWithFolding } from '#app/lib/strings'
  import { onChange } from '#app/lib/svelte/svelte'
  import { loadInternalLink } from '#app/lib/utils'
  import InputWithClearButton from '#components/input_with_clear_button.svelte'
  import Spinner from '#components/spinner.svelte'
  import ShelfInfo from '#inventory/components/shelf_info.svelte'
  import { updateItems } from '#inventory/lib/item_actions'
  import type { SerializedItem } from '#inventory/lib/items'
  import type { ShelfId } from '#server/types/shelf'
  import { getShelvesByOwner, getShelvesByIds, type SerializedShelf, shelfMatchesFilter } from '#shelves/lib/shelves'
  import { i18n, I18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'

  let {
    serializedItem = $bindable(),
    flash = $bindable(),
  }: {
    serializedItem: SerializedItem
    flash?: FlashState
  } = $props()

  const { _id: itemId, mainUserIsOwner } = serializedItem
  let shelvesIds = $state(serializedItem.shelves)
  let userShelves: SerializedShelf[] = $state([])
  let itemShelves: SerializedShelf[] = $state([])
  let hideUnselectedShelves = $state(false)

  let waitForShelves: Promise<void> = $state()
  if (mainUserIsOwner) {
    waitForShelves = getShelvesByOwner(mainUser?._id)
      .then(res => userShelves = res)
      .catch(err => flash = err)
  } else {
    // eslint-disable-next-line svelte/no-unused-svelte-ignore
    // svelte-ignore state_referenced_locally
    waitForShelves = getShelvesByIds(shelvesIds)
      .then(res => itemShelves = res)
      .catch(err => flash = err)
  }

  let selectedShelves: SerializedShelf[] = $state()
  let displayedShelves: SerializedShelf[] = $state()
  let shelvesFilter: string = $state('')
  const normalizedShelvesFilter = $derived(normalizeStringWithFolding(shelvesFilter))

  function updateDisplayedShelves () {
    selectedShelves = userShelves.filter(shelf => shelvesIds.includes(shelf._id))
    displayedShelves = hideUnselectedShelves ? selectedShelves : userShelves
  }
  $effect(() => {
    onChange(hideUnselectedShelves, shelvesIds, userShelves, updateDisplayedShelves)
  })

  const filteredDisplayedShelvesIds = $derived.by(() => {
    const ids = displayedShelves
      .filter(shelf => shelfMatchesFilter(shelf, normalizedShelvesFilter))
      .map(shelf => shelf._id)
    return new Set(ids)
  })
  // svelte-ignore state_referenced_locally
  let currentShelves: ShelfId[] = $state(shelvesIds)
  async function save () {
    try {
      if (isEqual(shelvesIds, currentShelves)) return
      serializedItem.shelves = currentShelves = shelvesIds
      await updateItems({
        items: [ itemId ],
        attribute: 'shelves',
        value: shelvesIds,
      })
    } catch (err) {
      flash = err
    }
  }

  const lazySave = debounce(save, 500)

  $effect(() => {
    onChange(shelvesIds, lazySave)
  })

  function toggleShownShelves () {
    hideUnselectedShelves = !hideUnselectedShelves
    shelvesFilter = ''
  }
</script>

<div class="item-shelves">
  {#if mainUserIsOwner}
    {#await waitForShelves}
      <Spinner /><span class="section-label">{I18n('shelves')}</span>
    {:then}
      {#if userShelves.length > 0}
        <div class="header">
          <span class="section-label">{I18n('shelves')}</span>
          {#if !hideUnselectedShelves && userShelves.length > 5}
            <InputWithClearButton
              bind:value={shelvesFilter}
              placeholder="Filter shelves…"
              count={filteredDisplayedShelvesIds.size}
              total={displayedShelves.length}
            />
          {/if}
        </div>
        <div class="shelves-options">
          {#each displayedShelves as shelf (shelf._id)}
            {@const shown = filteredDisplayedShelvesIds.has(shelf._id)}
            <label title={I18n('select_shelf')} class="shelf-option" class:shown>
              <input type="checkbox" bind:group={shelvesIds} value={shelf._id} />
              <ShelfInfo {shelf} />
            </label>
          {/each}
        </div>
        {#if selectedShelves.length < userShelves.length}
          <button
            onclick={toggleShownShelves}
            class:rotate={!hideUnselectedShelves}
          >
            {@html icon('chevron-down')}
            <span class="label">
              {#if hideUnselectedShelves}
                {i18n('Show non-selected shelves')}
              {:else}
                {i18n('Show only selected shelves')}
              {/if}
            </span>
          </button>
        {/if}
      {/if}
    {/await}
  {:else}
    {#if itemShelves?.length > 0}

      <ul>
        {#each itemShelves as shelf (shelf._id)}
          <li>
            <a
              href={shelf.pathname}
              onclick={loadInternalLink}
            >
              <ShelfInfo {shelf} />
            </a>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  @import "inventory/scss/shelves_selectors";

  .header{
    @include display-flex(row, center);
    .section-label{
      margin-inline-end: auto;
    }
  }
  .item-shelves:not(:empty){
    margin-block-start: 1em;
    background: $off-white;
    padding: 0.5em;
    @include radius;
    @include display-flex(column, stretch);
  }
  .section-label{
    padding: 0 0.5em;
    margin-block-end: 0.2em;
  }
  .shelves-options, ul{
    max-height: 16em;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .shelf-option{
    &:not(.shown){
      display: none;
    }
  }
  button{
    padding: 0.5em;
    @include shy(0.8);
    @include display-flex(row, center, center);
    :global(.fa){
      @include transition(transform, 0.2s);
    }
    .label{
      margin: 0 0.5rem;
    }
  }
  .rotate{
    :global(.fa){
      transform: scaleY(-1);
    }
  }
</style>
